import { useTranslation } from "react-i18next";

import type { components } from "@/api/types";
import { formatMoney } from "@/shared/lib";
import { Icon, type IconName, Panel } from "@/shared/ui";

type EarningsStatsData = components["schemas"]["EarningsStats"];

/**
 * Три цифры под графиком: сколько было начислений, среднее и лучший день.
 *
 * Все три приходят с бэкенда готовыми. Посчитать их здесь нельзя — правило ТЗ
 * №5 запрещает арифметику над деньгами на клиенте, — и это не формальность:
 * «среднее» считается по числу начислений, а не по числу дней, и разъехаться
 * этим двум определениям дешевле всего запретить.
 */
export function EarningsStats({ stats }: { stats: EarningsStatsData }) {
  const { t } = useTranslation();

  return (
    <section className="grid grid-cols-3 gap-1">
      <Stat
        icon="coinStack"
        label={t("earnings.stats.accruals")}
        value={String(stats.accruals)}
      />
      {/* Без суффикса «USDT»: валюта уже названа суммой прямо над этим рядом,
          а на трёх карточках в 12px она не помещается и обрезает само число —
          то есть съедает данные ради подписи, которая ничего не добавляет.
          В макете суффикс есть, но там кегль 9px, нечитаемый в WebView. */}
      <Stat
        icon="trendUp"
        label={t("earnings.stats.average")}
        value={formatMoney(stats.average, { sign: "always" })}
      />
      <Stat
        icon="star"
        label={t("earnings.stats.best")}
        value={formatMoney(stats.best_day, { sign: "always" })}
      />
    </section>
  );
}

function Stat({ icon, label, value }: { icon: IconName; label: string; value: string }) {
  return (
    <Panel tone="green" radius="md" className="flex items-center gap-1.5 px-1.5 py-2">
      <span className="flex size-[25px] shrink-0 items-center justify-center rounded-sm border border-status-profit/20 text-status-profit">
        <Icon name={icon} size={14} />
      </span>
      <span className="flex min-w-0 flex-col">
        {/* Подпись переносится, а не обрезается: «Лучший день» в три карточки
            по ширине не влезает одной строкой, и многоточие вместо слова
            превращает подпись в загадку. */}
        <span className="text-micro leading-tight text-text-secondary">{label}</span>
        <span className="truncate text-label font-semibold text-text-primary">{value}</span>
      </span>
    </Panel>
  );
}
